import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToast } from "../../contexts/ToastContext";
import * as api from "../../services/api";
import { timeAgo } from "../../utils/time";

// The small text actions under a comment. Padded so they're a fair thumb
// target even though they read as a caption.
const actionBase = "py-1 font-medium focus-visible:outline-none focus-visible:underline";
const actionClass = `${actionBase} text-muted-foreground hover:text-foreground focus-visible:text-foreground`;

/**
 * One comment on a feed post. Your own carry Edit and Delete underneath (#152);
 * an edited comment says so beside its time.
 */
export default function CommentItem({ comment, postId, isMine, onChanged }) {
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.content);
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const startEditing = () => {
    setDraft(comment.content);
    setConfirmingDelete(false);
    setEditing(true);
  };

  const save = async () => {
    const content = draft.trim();
    if (!content || busy) return;
    if (content === comment.content) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await api.updateComment(postId, comment.id, content);
      setEditing(false);
      await onChanged?.();
    } catch (err) {
      console.error("Edit comment failed:", err);
      showToast({ title: "Couldn't save your edit", description: "Check your connection and try again." });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteCommentApi(postId, comment.id);
      await onChanged?.();
    } catch (err) {
      console.error("Delete comment failed:", err);
      showToast({ title: "Couldn't delete that comment", description: "Check your connection and try again." });
      setBusy(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <div className="flex gap-2">
      {comment.author_photo ? (
        <img src={comment.author_photo} alt="" className="h-6 w-6 rounded-full mt-0.5" />
      ) : (
        <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-xs mt-0.5">
          {comment.author_name?.[0]}
        </div>
      )}
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-2">
            <Input
              autoFocus
              aria-label="Edit your comment"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
              className="text-sm"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={busy || !draft.trim()}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-muted/30 rounded-lg px-3 py-2">
            <p className="text-xs font-medium">{comment.author_name}</p>
            <p className="text-sm break-words">{comment.content}</p>
          </div>
        )}
        {!editing && (
          <div className="flex flex-wrap items-center gap-x-3 px-3 text-[11px] text-muted-foreground">
            {/* The question takes the time's place, so it stays on one line. */}
            {!confirmingDelete && (
              <span className="py-1">
                {timeAgo(comment.created_at)}
                {comment.edited && " · edited"}
              </span>
            )}
            {isMine &&
              (confirmingDelete ? (
                <>
                  <span className="py-1 text-foreground">Delete this comment?</span>
                  <button
                    type="button"
                    className={`${actionBase} text-destructive hover:text-destructive/80`}
                    onClick={remove}
                    disabled={busy}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className={actionClass}
                    onClick={() => setConfirmingDelete(false)}
                    disabled={busy}
                  >
                    Keep
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className={actionClass} onClick={startEditing}>
                    Edit
                  </button>
                  <button type="button" className={actionClass} onClick={() => setConfirmingDelete(true)}>
                    Delete
                  </button>
                </>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
